import React, { useEffect, useMemo, useState } from 'react'
import api from '../../../utils/api'
import { downloadByteArrayAsFile } from '../../../utils/arquivo'
import { dataHoraAtual } from '../../../utils/dateUtils'
import { useForm } from '@mantine/form'
import { Badge, Stack } from '@mantine/core'
import FiltroLicencaDispensa from '../FiltroLicencaDispensa'
import ExportButton from '../ExportButton'
import Tabela from '../Tabela'
import dayjs from 'dayjs'
import { getCorStatusLicencaDispensa } from '../../../utils/string'

const TabDis = () => {
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10
    })
    const [filtro, setFiltro] = useState({
        pagina: 0,
        tamanhoPagina: 10
    })
    const [data, setData] = useState([])
    const [total, setTotal] = useState(0)
    const [numerosDispensas, setNumerosDispensas] = useState([])

    const downloadCSV = () => {
        api.post(`/api/analise/transparencia/csv/dispensa`, numerosDispensas, {
            responseType: 'arraybuffer'
        }).then((response) => {
            downloadByteArrayAsFile(
                response.data,
                `Relatorio_Exportar_Dispensa_Transparencia_${dataHoraAtual()}.csv`,
                'text/csv'
            )
        })
    }

    const findDispensas = async (param) => {
        try {
            await findByFilter(param)
        } catch (error) {
            console.error('Error fetching data:', error)
        }
    }

    const findByFilter = async (param) => {
        try {
            const result = await api.post(
                '/api/analise/transparencia/dispensa/filtro',
                param
            )
            setData(result.data.dispensas.content)
            setTotal(result.data.dispensas.totalElements)
            setNumerosDispensas(result.data.numerosDispensas)
        } catch (error) {
            console.error('Error while filtering data:', error)
        }
    }

    const handleFilter = () => {
        findDispensas(filtro)
    }

    const form = useForm({
        initialValues: {
            cpfCnpjNome: '',
            numeroDocumento: '',
            numeroProcesso: '',
            numeroProtocolo: '',
            municipio: '',
            dataInicio: null,
            dataFinal: null
        }
    })

    const clearFilter = () => {
        form.setValues({
            cpfCnpjNome: '',
            numeroDocumento: '',
            numeroProcesso: '',
            numeroProtocolo: '',
            municipio: '',
            dataInicio: null,
            dataFinal: null
        })

        setFiltro({
            pagina: 0,
            tamanhoPagina: 10
        })

        const localResetFilter = {
            tamanhoPagina: 10,
            pagina: 0
        }

        findDispensas(localResetFilter)
    }

    const columnsTable = useMemo(
        () => [
            {
                accessorKey: 'numeroProcesso',
                header: 'Nº do Processo',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'numeroProtocolo',
                header: 'Nº do Protocolo',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'numeroDi',
                header: 'N° da DI',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'dataEntradaFormatada',
                header: 'Data de Formalização Protocolo',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'dataDispensaFormatada',
                header: 'Data da Emissão da DI',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'dataFinalVigenciaFormatada',
                header: 'Data de Vencimento da DI',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'situacao',
                header: 'Situação',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                },
                Cell: ({ cell }) => {
                    return (
                        <Badge
                            color={getCorStatusLicencaDispensa(
                                String(cell.getValue())
                            )}
                        >
                            {String(cell.getValue())}
                        </Badge>
                    )
                }
            },
            {
                accessorKey: 'codigoTipologia',
                header: 'Código da Tipologia',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'nomeTipologia',
                header: 'Descrição da Tipologia',
                size: 250,
                minSize: 250,
                maxSize: 250,
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'codigoAtividadeEmpreendimento',
                header: 'Código Ambiental',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'nomeAtividadeEmpreendimento',
                header: 'Descrição do Código Ambiental',
                size: 350,
                minSize: 350,
                maxSize: 350,
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'potencialPoluidor',
                header: 'Potencial Poluidor',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'porteEmpreendimento',
                header: 'Porte do Empreendimento',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'orgaoResponsavel',
                header: 'Órgão Responsável',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'nomeEmpreendedor',
                header: 'Nome do Empreendedor',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'cpfCnpjEmpreendedor',
                header: 'CPF/ CNPJ do Empreendedor',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'enderecoCompletoEmpreendimento',
                header: 'Endereço do Empreendimento',
                size: 300,
                minSize: 300,
                maxSize: 300,
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'municipio',
                header: 'Municípios do Empreendimento',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'cep',
                header: 'CEP do Empreendimento',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'latitude',
                header: 'Latitude',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'longitude',
                header: 'Longitude',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            },
            {
                accessorKey: 'responsavelTecnicoProcesso',
                header: 'Responsável Técnico do Processo',
                mantineTableBodyCellProps: {
                    align: 'left'
                },
                mantineTableHeadCellProps: {
                    align: 'left'
                }
            }
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    useEffect(() => {
        findDispensas(filtro)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (
            pagination.pageIndex !== filtro.pagina ||
            pagination.pageSize !== filtro.tamanhoPagina
        ) {
            const localFiltro = {
                ...filtro,
                tamanhoPagina: pagination.pageSize,
                pagina: pagination.pageIndex
            }
            setFiltro(localFiltro)
            findDispensas(localFiltro)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination])

    useEffect(() => {
        const filtroAtualizado = {
            ...filtro,
            numeroDocumento: form.values.numeroDocumento,
            stringPesquisa: form.values.cpfCnpjNome,
            processo: form.values.numeroProcesso,
            protocolo: form.values.numeroProtocolo,
            municipio: form.values.municipio,
            dataInicioPeriodo: form.values.dataInicio
                ? dayjs(form.values.dataInicio).format('YYYY-MM-DDTHH:mm:ss')
                : null,
            dataFimPeriodo: form.values.dataFinal
                ? dayjs(form.values.dataFinal).format('YYYY-MM-DDTHH:mm:ss')
                : null
        }

        setFiltro(filtroAtualizado)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.values])

    return (
        <Stack gap={'0'}>
            <FiltroLicencaDispensa
                clearFilter={clearFilter}
                form={form}
                handleFilter={handleFilter}
            />
            <ExportButton onExport={downloadCSV} />
            <Tabela
                columns={columnsTable}
                data={data}
                rowCount={total}
                title={'Listagem de DIs emitidas/pagas no SISLAM'}
                setPagination={setPagination}
                state={{
                    pagination: {
                        pageIndex: filtro.pagina,
                        pageSize: filtro.tamanhoPagina
                    }
                }}
            />
        </Stack>
    )
}

export default TabDis
